import type { BaseSchema } from 'yup';
import * as Yup from 'yup';
import IPCIDR from 'ip-cidr';
import * as Types from 'Generated/schema';
import {
  DetectionFilterConditionInput,
  DetectionFilterStatement,
  DetectionFilterStatementInput,
  FilterField,
} from 'Generated/schema';
import { GetFilterFields } from 'Source/graphql/queries/getFilterFields.generated';
import {
  ExtractedFilterConditions,
  FieldFilterOperators,
  FieldFilterTypes,
  FilterFieldWithGroupName,
  PANTHER_MANAGED_LABEL,
  SHARED_LABEL,
} from './constants';

const addGroupName = (filters: FilterField[], groupName): FilterFieldWithGroupName[] => {
  return filters.map(filter => {
    return { ...filter, groupName };
  });
};

export const addGroupNameToFilters = (data: GetFilterFields): FilterFieldWithGroupName[] =>
  data
    ? [
        ...addGroupName(data.filterFields.pantherManaged, PANTHER_MANAGED_LABEL),
        ...data.filterFields.filtersByLogType
          .map(group => addGroupName(group.filters, group.logType))
          .reduce((prev, current) => [...prev, ...current], []),
        ...addGroupName(data.filterFields.shared, SHARED_LABEL),
      ]
    : [];

export const extractFilterConditions = (
  filterData?: DetectionFilterStatement,
): ExtractedFilterConditions =>
  (filterData?.and || []).map(
    ({
      condition,
    }: {
      condition?: Types.Maybe<
        Pick<
          Types.DetectionFilterCondition,
          'source' | 'target' | 'operator' | 'value' | 'targetTypeHint'
        >
      >;
    }) => ({
      ...condition,
      ...(condition?.value ? { value: JSON.parse(condition?.value) } : {}),
    }),
  );

export const getFieldAndInputType = (
  filter: Pick<DetectionFilterConditionInput, 'target' | 'operator'>,
  filterableFields: FilterField[],
) => {
  const { target, operator } = filter;
  const fieldType =
    filterableFields.find(({ target: fieldTarget }) => target === fieldTarget)?.type || 'string';
  const operatorDetails = FieldFilterOperators.find(
    ({ value: operatorValue }) => operatorValue === operator,
  );
  const inputType =
    Object.keys(operatorDetails?.inputTypes || {}).length >= 1
      ? operatorDetails.inputTypes[fieldType]
      : null;
  return { fieldType, inputType, operatorDetails };
};

export const filtersToStatementInput = (
  filters: ExtractedFilterConditions,
  filterableFields: FilterFieldWithGroupName[],
): DetectionFilterStatementInput => {
  return {
    and: filters.map(
      ({ target, operator, value }: { target: string; operator: string; value: any }) => ({
        condition: {
          target,
          operator,
          value: JSON.stringify(
            typeof value === 'object' && value != null
              ? value.map(val => {
                  const { inputType } = getFieldAndInputType(
                    { target, operator },
                    filterableFields,
                  );
                  switch (inputType) {
                    case FieldFilterTypes.INT_ARRAY:
                      return parseInt(val, 10);
                    case FieldFilterTypes.FLOAT_ARRAY:
                      return parseFloat(val);
                    case FieldFilterTypes.IP_ARRAY:
                    case FieldFilterTypes.STRING_ARRAY:
                    default:
                      return val;
                  }
                })
              : value,
          ),
        },
      }),
    ),
  };
};

export const getFilterValueFieldLabelAndPlaceholder = (
  inputType: FieldFilterTypes,
): { label: string; placeholder: string } => {
  switch (inputType) {
    case FieldFilterTypes.INT:
      return { label: 'Integer', placeholder: 'Enter an integer...' };
    case FieldFilterTypes.FLOAT:
      return {
        label: 'Floating Point Number',
        placeholder: `Enter a floating point number (${(1.2).toLocaleString()})...`,
      };

    case FieldFilterTypes.IP:
      return { label: 'IP', placeholder: 'Enter an IP address (xxx.xxx.xxx.xxx)...' };
    case FieldFilterTypes.CIDR:
      return { label: 'CIDR Block', placeholder: 'Enter a CIDR block...' };
    case FieldFilterTypes.BOOL:
      return { label: 'Boolean', placeholder: 'Select "true" or "false"...' };
    case FieldFilterTypes.BOOL_ARRAY:
      return { label: 'List', placeholder: 'Enter a list of booleans...' };
    case FieldFilterTypes.STRING_ARRAY:
      return { label: 'List', placeholder: 'Enter a list of strings...' };
    case FieldFilterTypes.INT_ARRAY:
      return { label: 'List', placeholder: 'Enter a list of integers...' };
    case FieldFilterTypes.FLOAT_ARRAY:
      return { label: 'List', placeholder: 'Enter a list of floating point numbers...' };
    case FieldFilterTypes.IP_ARRAY:
      return { label: 'List', placeholder: 'Enter a list of IPs...' };
    case FieldFilterTypes.STRING:
    default:
      return { label: 'String', placeholder: 'Enter a string...' };
  }
};

const getFilterValidationErrorMessage = (inputType: FieldFilterTypes): string => {
  switch (inputType) {
    case FieldFilterTypes.INT:
      return 'This field + operator requires an integer';
    case FieldFilterTypes.FLOAT:
      return 'This field + operator requires a floating point number';
    case FieldFilterTypes.STRING:
      return 'This field + operator requires a value';
    case FieldFilterTypes.IP:
    case FieldFilterTypes.CIDR:
      return 'This field + operator requires an IP or CIDR';
    case FieldFilterTypes.BOOL:
      return 'This field + operator requires true or false';
    case FieldFilterTypes.BOOL_ARRAY:
      return 'This field + operator require one or more "true" or "false" values';
    case FieldFilterTypes.STRING_ARRAY:
      return 'This field + operator requires one or more values';
    case FieldFilterTypes.INT_ARRAY:
      return 'This field + operator requires one or more integers';
    case FieldFilterTypes.FLOAT_ARRAY:
      return 'This field + operator requires one or more floating point numbers';
    case FieldFilterTypes.IP_ARRAY:
      return 'This field + operator requires one or more IPs or CIDRs';
    default:
      return '';
  }
};

export const filterValueCondition =
  (flatFilters: FilterField[]) =>
  (target: string, operator: string, schema: BaseSchema): BaseSchema => {
    const { inputType } = getFieldAndInputType({ target, operator }, flatFilters);

    return schema.test(
      `${target} + ${operator} = ${inputType} validation`,
      getFilterValidationErrorMessage(inputType),
      value => {
        switch (inputType) {
          case FieldFilterTypes.INT:
            // Yup's `integer()` is only valid for positive integers, and cast() blows
            // up on NaN, so there's this hack.
            return !Number.isNaN(Number(value)) && Number(value) === Math.round(Number(value));
          case FieldFilterTypes.FLOAT:
            return !Number.isNaN(Number(value));
          case FieldFilterTypes.STRING:
            return Yup.string().isType(value);
          case FieldFilterTypes.IP:
          case FieldFilterTypes.CIDR:
            return Yup.string().isType(value) && IPCIDR.isValidAddress(value);
          case FieldFilterTypes.BOOL:
            return Yup.boolean().isType(value);
          case FieldFilterTypes.BOOL_ARRAY:
            return Yup.array().of(Yup.boolean()).isType(value);
          case FieldFilterTypes.STRING_ARRAY:
            return Yup.array().of(Yup.string()).isType(value);
          case FieldFilterTypes.INT_ARRAY:
            return (
              Yup.array().isType(value) &&
              value.filter(
                int => Number.isNaN(Number(int)) || Number(int) !== Math.round(Number(int)),
              ).length === 0
            );
          case FieldFilterTypes.FLOAT_ARRAY:
            return (
              Yup.array().isType(value) &&
              value.filter(float => !Number.isNaN(Number(float))).length === 0
            );
          case FieldFilterTypes.IP_ARRAY:
            return (
              Yup.array().of(Yup.string()).isType(value) &&
              value.filter(ip => !IPCIDR.isValidAddress(ip)).length === 0
            );
          default:
            return value === undefined;
        }
      },
    );
  };

const fieldFilterTypeOfValue = (
  value: Array<string> | Array<number> | Array<boolean> | string | number | null,
): FieldFilterTypes | null => {
  switch (typeof value) {
    case 'string':
      if (IPCIDR.isValidAddress(value)) {
        return IPCIDR.isValidCIDR(value) ? FieldFilterTypes.CIDR : FieldFilterTypes.IP;
      }
      return FieldFilterTypes.STRING;
    case 'bigint':
    case 'number':
      return Number(value) !== Math.round(Number(value))
        ? FieldFilterTypes.FLOAT
        : FieldFilterTypes.INT;
    case 'boolean':
      return FieldFilterTypes.BOOL;
    case 'object':
      if (value instanceof Array) {
        switch (typeof value[0]) {
          case 'string':
            return IPCIDR.isValidAddress(value[0])
              ? FieldFilterTypes.IP_ARRAY
              : FieldFilterTypes.STRING_ARRAY;
          case 'bigint':
          case 'number':
            return (value as Array<number>).filter(val => val.toString().indexOf('.') >= 0).length >
              0
              ? FieldFilterTypes.FLOAT_ARRAY
              : FieldFilterTypes.INT_ARRAY;
          case 'boolean':
            return FieldFilterTypes.BOOL_ARRAY;
          case 'object':
          case 'symbol':
          case 'undefined':
          case 'function':
          default:
            return null;
        }
      }
      return null;
    case 'symbol':
    case 'undefined':
    case 'function':
    default:
      return null;
  }
};

export type FindOperatorForFieldOptions = {
  value?: Array<string> | Array<number> | Array<boolean> | string | number | null;
  operators?: typeof FieldFilterOperators;
  preferredOperatorValues?: Array<string>;
};

export const findOperatorForField = (
  fieldType: FieldFilterTypes,
  options?: FindOperatorForFieldOptions,
): string => {
  const {
    value = '',
    operators = FieldFilterOperators,
    preferredOperatorValues = ['!contains', '!=', '<'],
  } = options;
  const desiredInputType = fieldFilterTypeOfValue(value);
  const supportedOperatorValues = operators.reduce(
    (supportedOperators, op) =>
      op.fieldTypes.includes(fieldType) &&
      (!desiredInputType || op.inputTypes[fieldType]?.includes(desiredInputType))
        ? [...supportedOperators, op.value]
        : supportedOperators,
    [],
  );
  return (
    supportedOperatorValues.find(op => preferredOperatorValues.includes(op)) ||
    supportedOperatorValues[0]
  );
};
